import React, { useCallback, useState } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import { toast } from 'react-toastify';
import { useCreateCompany } from '@area-restrita/api-client/endpoints/companies/companies.gen';

import { useDialog } from '@/components/template/Dialog';

import { ICompanyFormSchema } from './steps/CompanyFormStep/schema';
import { ICompanyResponsibleFormSchema } from './steps/ResponsibleFormStep/schema';
import { ICompanyAddressFormSchema } from './steps/AddressFormStep/schema';
import AddCompanyDialogView from './view';
import { AddCompanyStep } from './steps';

const AddCompany: React.FC = () => {
  const { handleClose } = useDialog();
  const queryClient = useQueryClient();

  const [currentStep, setCurrentStep] = useState<AddCompanyStep>({
    step: 'company',
  });

  const createCompany = useCreateCompany({
    mutation: {
      onSuccess: async () => {
        toast.success('Empresa criada com sucesso');
        await queryClient.invalidateQueries({
          queryKey: ['/api/v1/companies'],
        });
        handleClose();
      },
      onError: () => {
        toast.error('Falha ao criar a empresa');
      },
    },
  });

  const handlePrevStep = useCallback(() => {
    setCurrentStep((step) => {
      if (step.step === 'responsible') return { ...step, step: 'company' };
      if (step.step === 'address') return { ...step, step: 'responsible' };
      return step;
    });
  }, []);

  const handleCompanySubmit = useCallback((company: ICompanyFormSchema) => {
    setCurrentStep((step) => ({ ...step, step: 'responsible', company }));
  }, []);

  const handleResponsibleSubmit = useCallback(
    (responsible: ICompanyResponsibleFormSchema) => {
      setCurrentStep((step) => {
        if (step.step !== 'responsible') return step;

        return {
          ...step,
          step: 'address',
          responsible,
        };
      });
    },
    [],
  );

  const handleAddressSubmit = useCallback(
    async ({ city, state, ...address }: ICompanyAddressFormSchema) => {
      if (currentStep.step !== 'address') return;

      await createCompany.mutateAsync({
        data: {
          address: {
            cityId: city.value,
            ...address,
          },
          ...currentStep.company,
          ...currentStep.responsible,
        },
      });
    },
    [currentStep, createCompany],
  );

  return (
    <AddCompanyDialogView
      isLoading={createCompany.isPending}
      currentStep={currentStep}
      onPrevStep={handlePrevStep}
      onCompanySubmit={handleCompanySubmit}
      onResponsibleSubmit={handleResponsibleSubmit}
      onAddressSubmit={handleAddressSubmit}
    />
  );
};

export default AddCompany;
